/**
 * Regelbedarfe 2026 nach § 20 SGB II i.V.m. Regelbedarfsstufen-Verordnung.
 *
 * **Quellenhinweise** und Verifikations-TODO:
 *
 * Die hier hinterlegten Werte entsprechen der im Konzeptdokument §5.3 und im
 * Architektur-Plan AP5 vorgegebenen Tabelle (Stand „verifizieren via § 20
 * SGB II + Regelbedarfsstufen-Verordnung 2026"). Da der gesetzgeberische
 * Anpassungsmechanismus (§ 20 Abs. 5 SGB II) jährlich zum 1. Januar greift,
 * sind diese Konstanten **bis zur Verifikation gegen das Bundesgesetzblatt
 * 2026 als Arbeitswerte** zu betrachten.
 *
 * TODO(legal-verification): Vor Live-Schalten der Stellungnahmen die Werte
 *   gegen die offiziell veröffentlichte Regelbedarfsstufen-Fortschreibungs-
 *   verordnung 2026 abgleichen. Aufgabe für `docs/DSGVO/`-Anhang
 *   „Regelbedarfsstufen-Versionierung.md".
 *
 * Werte sind in **Cent** (Integer), nie in Float-Euro — siehe
 * `packages/core/src/schema/brands.ts`.
 */
import type { MoneyCents } from '../schema/brands.js';

/**
 * Regelbedarfsstufen 1-6 nach Anlage zu § 28 SGB XII / § 8 RBEG.
 *
 *   RBS 1: Alleinstehende und Alleinerziehende, Erwachsene mit minder-
 *          jährigem Partner.                                              563€
 *   RBS 2: Erwachsene Partner in Bedarfsgemeinschaft (90% von RBS 1).     506€
 *   RBS 3: Erwachsene unter 25 ohne eigenen Haushalt; Erwachsene in
 *          stationären Einrichtungen (80% von RBS 1).                     451€
 *   RBS 4: Jugendliche 14–17 Jahre.                                       471€
 *   RBS 5: Kinder 6–13 Jahre.                                             390€
 *   RBS 6: Kinder 0–5 Jahre.                                              357€
 */
export const REGELBEDARFE_2026_CENTS: Readonly<{
  RBS1: MoneyCents;
  RBS2: MoneyCents;
  RBS3: MoneyCents;
  RBS4: MoneyCents;
  RBS5: MoneyCents;
  RBS6: MoneyCents;
}> = {
  RBS1: 56_300 as MoneyCents,
  RBS2: 50_600 as MoneyCents,
  RBS3: 45_100 as MoneyCents,
  RBS4: 47_100 as MoneyCents,
  RBS5: 39_000 as MoneyCents,
  RBS6: 35_700 as MoneyCents,
};

/**
 * Grundfreibetrag bei Erwerbseinkommen nach § 11b Abs. 2 SGB II.
 * Ab dem ersten Euro Bruttoeinkommen wird ein Grundfreibetrag von 100€
 * abgezogen (Pauschale für Werbungskosten + Versicherungen + Riester).
 */
export const GRUNDFREIBETRAG_CENTS: MoneyCents = 10_000 as MoneyCents;

/**
 * Gestaffelte Erwerbstätigen-Freibeträge nach § 11b Abs. 3 SGB II.
 *   - 100,01–520€ Brutto:    20% bleiben anrechnungsfrei
 *   - 520,01–1.000€ Brutto:  30% bleiben anrechnungsfrei
 *   - 1.000,01–1.200€ Brutto: 10% bleiben anrechnungsfrei
 *   (Mit Kindern in der BG verlängert sich die 10%-Stufe bis 1.500€.)
 */
export const FREIBETRAG_TIERS = {
  TIER_1_UPPER_CENTS: 52_000, // 520€
  TIER_2_UPPER_CENTS: 100_000, // 1.000€
  TIER_3_UPPER_CENTS_NO_KIDS: 120_000, // 1.200€
  TIER_3_UPPER_CENTS_WITH_KIDS: 150_000, // 1.500€
  TIER_1_PERCENT: 0.2,
  TIER_2_PERCENT: 0.3,
  TIER_3_PERCENT: 0.1,
} as const;
